import React from 'react';
import { Link } from 'react-router-dom';
import { Lock, Shield, AlertTriangle, CheckCircle, FileQuestion, Users, TrendingUp } from 'lucide-react';
import SEO from '@/components/SEO';

const WhyIAM = () => {
  const withoutIAM = [
    { title: 'Shared and weak credentials', desc: 'Admin accounts shared across people; passwords reused or weak. One compromise can expose multiple systems.' },
    { title: 'Manual provisioning and deprovisioning', desc: 'New hires get accounts created by hand; leavers keep access until someone remembers to turn it off.' },
    { title: 'No visibility into who has access', desc: 'You cannot easily answer "who can access what." Spreadsheets are outdated the day they are built.' },
    { title: 'Ad-hoc or missing access reviews', desc: 'Certification happens in spreadsheets, or not at all. Auditors and customers expect evidence of regular reviews.' },
    { title: 'MFA gaps', desc: 'Some apps have MFA, others do not. Attackers target the weakest link.' },
    { title: 'Audit and sales friction', desc: 'Security questionnaires and due diligence expose gaps. Deals slow down or require last-minute scrambling.' },
  ];

  const risks = [
    { icon: Shield, title: 'Security', items: ['Credential theft and account takeover', 'Lateral movement after a breach', 'Insider risk from stale access'] },
    { icon: FileQuestion, title: 'Compliance', items: ['SOC 2, ISO 27001, and customer questionnaires expect access controls', 'No evidence = no pass', 'Remediation under time pressure'] },
    { icon: TrendingUp, title: 'Operational', items: ['IT and HR spend time on manual access tasks', 'Scaling headcount makes the problem worse', 'Incidents take longer to contain'] },
    { icon: Users, title: 'Reputational', items: ['Customers and partners lose trust after a breach', 'Investors ask about identity and access', 'Winning enterprise deals depends on documented controls'] },
  ];

  const withIAM = [
    'One identity per person; SSO and MFA everywhere.',
    'Automated joiner/mover/leaver; access removed when people leave.',
    'Clear view of who has access to what; audit logs and reports.',
    'Structured access reviews with evidence for auditors.',
    'Documented controls that map to SOC 2 and customer questionnaires.',
    'Faster sales cycles and due diligence when you can show how access is managed.',
  ];

  const questions = [
    'Do you enforce MFA on all applications?',
    'How do you manage access when someone leaves?',
    'How often do you review who has access to what?',
    'Can you show evidence of access reviews for our audit?',
    'Do you use SSO? How do you prevent shared passwords?',
  ];

  return (
    <div>
      <SEO
        title="Why IAM Matters - The Business Case for Identity Management"
        description="Learn why Identity and Access Management (IAM) is critical for business security, compliance, and growth. Discover the risks of poor IAM and benefits of proper implementation."
        keywords="why IAM matters, IAM business case, identity management benefits, access control importance, IAM ROI, cybersecurity investment"
        ogTitle="Why IAM Matters - The Business Case for Identity Management"
        ogDescription="Learn why Identity and Access Management (IAM) is critical for business security, compliance, and growth. Discover the risks of poor IAM and benefits of proper implementation."
        canonicalUrl="https://idnty.one/why-iam"
      />
      {/* Hero */}
      <section className="section-padding" style={{ background: 'var(--white)' }}>
        <div className="container">
          <h1 className="hero-title" style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
            WHY IAM MATTERS
          </h1>
          <p style={{ fontSize: '1.2rem', textAlign: 'center', maxWidth: '720px', margin: '0 auto 2rem', lineHeight: '1.8' }}>
            For organizations without structured identity and access management, the gap is not just technical — it affects security, compliance, and growth. Here is why IAM should be on your roadmap.
          </p>
          <div style={{ textAlign: 'center' }}>
            <Link to="/pricing" className="btn" style={{ marginRight: '0.75rem' }}>
              View IAM Tiers
            </Link>
            <Link to="/contact" className="btn btn-secondary">
              Talk to Us
            </Link>
          </div>
        </div>
      </section>

      {/* Life without IAM */}
      <section className="section-padding" style={{ background: 'var(--light-gray)' }}>
        <div className="container">
          <h2 className="section-title" style={{ textAlign: 'center', marginBottom: '1rem' }}>
            WITHOUT IAM: WHAT ACTUALLY HAPPENS
          </h2>
          <p style={{ textAlign: 'center', marginBottom: '2.5rem', fontSize: '1rem', maxWidth: '640px', margin: '0 auto 2.5rem' }}>
            Many growing companies run on manual processes and spreadsheets. These are the typical results.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '1.25rem' }}>
            {withoutIAM.map((item, i) => (
              <div key={i} className="card-heavy" style={{ background: 'var(--white)' }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem' }}>
                  <AlertTriangle size={24} style={{ color: 'var(--gold)', flexShrink: 0, marginTop: '0.2rem' }} />
                  <div>
                    <h3 className="feature-heading" style={{ fontSize: '1.1rem', marginBottom: '0.5rem' }}>{item.title}</h3>
                    <p style={{ fontSize: '0.95rem', lineHeight: '1.6', margin: 0 }}>{item.desc}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* The risks */}
      <section className="section-padding" style={{ background: 'var(--charcoal)', color: 'var(--white)' }}>
        <div className="container">
          <h2 className="section-title" style={{ textAlign: 'center', marginBottom: '2rem', color: 'var(--white)' }}>
            THE RISKS OF GOING WITHOUT
          </h2>
          <div className="grid-2">
            {risks.map((r, i) => {
              const Icon = r.icon;
              return (
                <div key={i} style={{ border: '2px solid rgba(255,255,255,0.3)', padding: '2rem', background: 'rgba(255,255,255,0.05)' }}>
                  <Icon size={40} style={{ color: 'var(--gold)', marginBottom: '1rem' }} />
                  <h3 className="feature-heading" style={{ color: 'var(--gold)', fontSize: '1.2rem', marginBottom: '1rem' }}>{r.title}</h3>
                  <ul style={{ paddingLeft: '1.25rem', margin: 0, fontSize: '0.95rem', lineHeight: '1.8' }}>
                    {r.items.map((item, j) => (
                      <li key={j}>{item}</li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* With IAM: outcomes */}
      <section className="section-padding" style={{ background: 'var(--white)' }}>
        <div className="container">
          <h2 className="section-title" style={{ textAlign: 'center', marginBottom: '1rem' }}>
            WITH IAM: WHAT CHANGES
          </h2>
          <p style={{ textAlign: 'center', marginBottom: '2rem', fontSize: '1rem', maxWidth: '600px', margin: '0 auto 2rem' }}>
            Structured identity and access management turns ad-hoc processes into documented, repeatable controls.
          </p>
          <div style={{ maxWidth: '700px', margin: '0 auto' }}>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
              {withIAM.map((item, i) => (
                <li key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem', marginBottom: '1.25rem' }}>
                  <CheckCircle size={22} style={{ color: 'var(--success)', flexShrink: 0, marginTop: '0.2rem' }} />
                  <span style={{ fontSize: '1.05rem', lineHeight: '1.6' }}>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* The questions they will ask */}
      <section className="section-padding" style={{ background: 'var(--light-gray)' }}>
        <div className="container">
          <h2 className="section-title" style={{ textAlign: 'center', marginBottom: '1rem' }}>
            THE QUESTIONS YOU WILL BE ASKED
          </h2>
          <p style={{ textAlign: 'center', marginBottom: '2rem', fontSize: '1rem', maxWidth: '560px', margin: '0 auto 2rem' }}>
            Customers, partners, and investors will ask about access control. Without IAM, answering is harder.
          </p>
          <div style={{ border: '4px solid var(--black)', padding: '2rem', background: 'var(--white)', maxWidth: '640px', margin: '0 auto' }}>
            <ul style={{ paddingLeft: '1.5rem', margin: 0, fontSize: '1.05rem', lineHeight: '2' }}>
              {questions.map((q, i) => (
                <li key={i} style={{ marginBottom: '0.5rem' }}>{q}</li>
              ))}
            </ul>
            <p style={{ marginTop: '1.5rem', marginBottom: 0, fontSize: '0.95rem', fontWeight: 'bold' }}>
              With IAM in place, you can answer each of these with evidence.
            </p>
          </div>
        </div>
      </section>

      {/* Who this is for */}
      <section className="section-padding" style={{ background: 'var(--charcoal)', color: 'var(--white)' }}>
        <div className="container">
          <h2 className="section-title" style={{ textAlign: 'center', marginBottom: '1.5rem', color: 'var(--white)' }}>
            WHO THIS APPLIES TO
          </h2>
          <p style={{ textAlign: 'center', marginBottom: '2rem', fontSize: '1.05rem', maxWidth: '720px', margin: '0 auto 2rem' }}>
            IAM matters most when you are past the earliest stage but not yet ready to hire a dedicated security team. You are closing larger deals, handling more data, and being asked to prove how you manage access.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '1.5rem' }}>
            <div style={{ border: '2px solid var(--gold)', padding: '1.5rem' }}>
              <div className="label" style={{ color: 'var(--gold)', marginBottom: '0.5rem' }}>Typical profile</div>
              <p style={{ margin: 0, fontSize: '0.95rem', lineHeight: '1.6' }}>Between first paying customer and first dedicated security hire. Relying on IT, eng, or ops for access today.</p>
            </div>
            <div style={{ border: '2px solid var(--gold)', padding: '1.5rem' }}>
              <div className="label" style={{ color: 'var(--gold)', marginBottom: '0.5rem' }}>Triggers</div>
              <p style={{ margin: 0, fontSize: '0.95rem', lineHeight: '1.6' }}>SOC 2 or compliance goal, customer security questionnaire, investor due diligence, or a near-miss that highlighted access risk.</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding" style={{ background: 'var(--black)', color: 'var(--white)' }}>
        <div className="container" style={{ textAlign: 'center' }}>
          <h2 className="section-title" style={{ color: 'var(--white)', marginBottom: '1rem' }}>
            READY TO CLOSE THE GAP?
          </h2>
          <p style={{ fontSize: '1.1rem', marginBottom: '1.5rem' }}>
            We implement structured IAM so you can answer the hard questions and scale without the risk.
          </p>
          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
            <Link to="/pricing" className="btn btn-gold">
              View IAM Tiers
            </Link>
            <Link to="/contact" className="btn btn-secondary">
              Schedule Consultation
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default WhyIAM;
